import { useState, useCallback, useEffect } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { MapLibraries } from '../types/GoogleMapsApi';
import { PlaceResultType } from '../types/placeResultLocation';
import { MARKET } from '../constants/market';

export interface UseGeocodingReturn {
  getCoordinates: (zipcode: string) => Promise<PlaceResultType | null>;
  zipCoordinates: PlaceResultType | null;
  loading: boolean;
  error: string | null;
  isLoaded: boolean;
  loadError: Error | undefined;
}
export const useGetLocationByZip = (initialZipcode?: string): UseGeocodingReturn => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY ?? '',
    libraries: MapLibraries,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zipCoordinates, setZipCoordinates] = useState<PlaceResultType | null>(null);
  const getCoordinates = useCallback(
    async (zipcode: string) => {
      if (!isLoaded || !zipcode) return null;
      if (loadError) {
        setError('Google Maps failed to load');
        return null;
      }
      setLoading(true);
      setError(null);
      try {
        const geocoder = new google.maps.Geocoder();
        const response = await geocoder.geocode({
          address: zipcode,
          componentRestrictions: {
            country: MARKET === 'CAN' ? 'CA' : 'US',
            postalCode: zipcode,
          },
          region: MARKET === 'CAN' ? 'ca' : 'us',
        });
        console.log('RESPONSE ------>>>>', response);
        if (response.results[0]) {
          const addressComponents = response.results[0].address_components;
          const city =
            addressComponents?.find((component) => component.types.includes('locality'))
              ?.long_name || '';

          const region =
            addressComponents?.find((component) =>
              component.types.includes('administrative_area_level_1'),
            )?.long_name || '';

          const postalCode =
            addressComponents?.find((component) => component.types.includes('postal_code'))
              ?.long_name || '';

          const parsedPlace: PlaceResultType = {
            formattedAddress: response.results[0].formatted_address,
            name: response.results[0].formatted_address,
            placeId: response.results[0].place_id,
            types: response.results[0].types,
            city,
            region,
            postalCode,
            geometry: {
              location: {
                lat: response.results[0].geometry?.location?.lat(),
                lng: response.results[0].geometry?.location?.lng(),
              },
            },
          };
          setZipCoordinates(parsedPlace);
        }
        throw new Error('No results found');
      } catch (errorApi) {
        setError(errorApi instanceof Error ? errorApi.message : 'An error occurred');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isLoaded, loadError],
  );
  useEffect(() => {
    if (!initialZipcode) return;
    if (initialZipcode && isLoaded && !zipCoordinates) {
      console.log(' ------>>>> TRIGGER TO GRAB LOCATION BY ZIP <<<<------ ');
      getCoordinates(initialZipcode);
    }
  }, [isLoaded, initialZipcode, getCoordinates, zipCoordinates]);

  return { getCoordinates, zipCoordinates, loading, error, isLoaded, loadError };
};
